import { UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Col, Flex, Input, Row, Select, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CopyableLink } from "../../../../components/CopyableLink.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  invalidPhoneMessage,
  isValidPhone
} from "../../../../utils/validation/validate-phone.ts"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { pickChangedFields } from "../../../Lead/LeadWorkspacePage/lead-workspace-patch.ts"
import { LeadOverviewField } from "../../../Lead/LeadWorkspacePage/overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { initialsOf } from "../../../Lead/LeadWorkspacePage/overview/overview-initials.ts"
import type { Project, ProjectUpdateData } from "../../types/project.types.ts"
import {
  type ProjectOverviewCustomerValues,
  isLinkedProjectCustomer,
  linkedProjectCustomerId,
  projectCustomerName,
  projectCustomerValues
} from "./project-overview-customer.ts"

const { Text } = Typography

interface ProjectCustomerCardValues {
  customer: ProjectOverviewCustomerValues
  preferred_contact_method: string | null
}

interface ProjectCustomerInformationCardProps {
  project: Project
  filterOptions: Record<string, any>
  canEdit?: boolean
  onSave: (changed: Partial<ProjectUpdateData>) => Promise<boolean>
}

function ProjectCustomerInformationCard({
  project,
  filterOptions,
  canEdit,
  onSave
}: ProjectCustomerInformationCardProps) {
  const { t } = useTranslation()

  const customer = projectCustomerValues(project)
  const name = projectCustomerName(project)
  const linked = isLinkedProjectCustomer(project)
  const linkedId = linkedProjectCustomerId(project)

  const values: ProjectCustomerCardValues = {
    customer,
    preferred_contact_method: project?.preferred_contact_method ?? null
  }

  const contactMethodLabel =
    filterOptions?.contact_method?.find(
      (option: { value?: string; label?: string }) =>
        option?.value === project?.preferred_contact_method
    )?.label ?? project?.preferred_contact_method

  // The whole `customer` object counts as "changed" the moment any one of its
  // fields differs (WorkspaceCard diffs by top-level key), so it has to be
  // re-diffed here to send only the subfields that actually moved.
  const saveCustomer = (changed: Partial<ProjectCustomerCardValues>) => {
    const patch: Partial<ProjectUpdateData> = {}

    if ("preferred_contact_method" in changed) {
      patch.preferred_contact_method = changed.preferred_contact_method
    }

    if (changed.customer) {
      const edited = pickChangedFields(customer, changed.customer)
      if (Object.keys(edited).length) {
        patch.customer = edited as ProjectUpdateData["customer"]
      }
    }

    if (!Object.keys(patch).length) return Promise.resolve(true)

    return onSave(patch)
  }

  return (
    <WorkspaceCard<ProjectCustomerCardValues>
      cardId={"project-customer-information"}
      title={
        <OverviewCardTitle icon={<UserOutlined />} accent={"teal"}>
          {t("projectWorkspace.cards.customer.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("projectWorkspace.cards.customer.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      validate={({ customer }) =>
        isValidPhone(customer.phone) && isValidPhone(customer.phone_2)
      }
      onSave={saveCustomer}
      extra={
        linkedId ? (
          <Link to={RoutesPaths.customers.view(linkedId)}>
            <Button size={"small"}>
              {t("projectWorkspace.cards.customer.viewFull")}
            </Button>
          </Link>
        ) : null
      }
      renderEdit={({ values, setValues, saving }) => (
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.firstName")}
            >
              <Input
                autoFocus
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.firstName")}
                value={values.customer.first_name ?? ""}
                onChange={(event) =>
                  setValues({
                    customer: {
                      ...values.customer,
                      first_name: event.target.value || null
                    }
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.lastName")}
            >
              <Input
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.lastName")}
                value={values.customer.last_name ?? ""}
                onChange={(event) =>
                  setValues({
                    customer: {
                      ...values.customer,
                      last_name: event.target.value || null
                    }
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.email")}
            >
              <Input
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.email")}
                value={values.customer.email ?? ""}
                onChange={(event) =>
                  setValues({
                    customer: {
                      ...values.customer,
                      email: event.target.value || null
                    }
                  })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.contactMethod")}
            >
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.contactMethod")}
                options={filterOptions?.contact_method ?? []}
                value={values.preferred_contact_method ?? undefined}
                onChange={(value) =>
                  setValues({ preferred_contact_method: value ?? null })
                }
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.primaryPhone")}
            >
              <PhoneInput
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.primaryPhone")}
                status={isValidPhone(values.customer.phone) ? undefined : "error"}
                value={values.customer.phone ?? ""}
                onChange={(event) =>
                  setValues({
                    customer: {
                      ...values.customer,
                      phone: event.target.value || null
                    }
                  })
                }
              />
              {!isValidPhone(values.customer.phone) && (
                <Text type={"danger"} role={"alert"}>
                  {invalidPhoneMessage}
                </Text>
              )}
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.secondaryPhone")}
            >
              <PhoneInput
                disabled={saving}
                aria-label={t("projectWorkspace.cards.customer.secondaryPhone")}
                status={
                  isValidPhone(values.customer.phone_2) ? undefined : "error"
                }
                value={values.customer.phone_2 ?? ""}
                onChange={(event) =>
                  setValues({
                    customer: {
                      ...values.customer,
                      phone_2: event.target.value || null
                    }
                  })
                }
              />
              {!isValidPhone(values.customer.phone_2) && (
                <Text type={"danger"} role={"alert"}>
                  {invalidPhoneMessage}
                </Text>
              )}
            </LeadOverviewField>
          </Col>
        </Row>
      )}
    >
      <Flex vertical gap={22}>
        <Flex gap={12} align={"center"} wrap>
          {!!name && (
            <Avatar className={"bg-primary shrink-0"} size={48}>
              {initialsOf(name)}
            </Avatar>
          )}
          <Text
            className={"min-w-0 truncate text-[21px] font-semibold leading-tight"}
          >
            {name || t("projectWorkspace.cards.customer.unnamed")}
          </Text>
          <Tag
            color={linked ? "green" : "default"}
            className={"shrink-0"}
            data-testid={"project-overview-customer-link"}
          >
            {linked
              ? t("projectWorkspace.cards.customer.linked")
              : t("projectWorkspace.cards.customer.unlinked")}
          </Tag>
        </Flex>

        <Row gutter={[48, 18]}>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.email")}
              value={customer.email}
            >
              <span className={"text-sm font-medium"}>
                <CopyableLink type={"email"} href={customer.email!}>
                  {customer.email}
                </CopyableLink>
              </span>
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.primaryPhone")}
              value={customer.phone}
            >
              <span className={"text-sm font-medium"}>
                <CopyableLink type={"phone"} href={customer.phone!}>
                  {customer.phone}
                </CopyableLink>
              </span>
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.secondaryPhone")}
              value={customer.phone_2}
            >
              <span className={"text-sm font-medium"}>
                <CopyableLink type={"phone"} href={customer.phone_2!}>
                  {customer.phone_2}
                </CopyableLink>
              </span>
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("projectWorkspace.cards.customer.contactMethod")}
              value={contactMethodLabel}
            />
          </Col>
        </Row>
      </Flex>
    </WorkspaceCard>
  )
}

export { type ProjectCustomerCardValues, ProjectCustomerInformationCard }
